import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  Files,
  UploadSimple,
  Download,
  Trash,
  CircleNotch,
  FilePdf,
  FileImage,
  FileDoc,
  File,
  Eye,
  FunnelSimple,
  MagnifyingGlass
} from '@phosphor-icons/react';


const FILE_ICONS = {
  'pdf': FilePdf,
  'png': FileImage,
  'jpg': FileImage,
  'jpeg': FileImage,
  'webp': FileImage,
  'doc': FileDoc,
  'docx': FileDoc,
};

function getFileIcon(filename) {
  const ext = filename?.split('.').pop()?.toLowerCase() || '';
  return FILE_ICONS[ext] || File;
}

function formatFileSize(bytes) {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

export default function DocumentsPage() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [analyzing, setAnalyzing] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);

  useEffect(() => { fetchDocuments(); }, [categoryFilter]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const params = categoryFilter ? `?category=${encodeURIComponent(categoryFilter)}` : '';
      const response = await axios.get(`${API_URL}/api/documents${params}`, { withCredentials: true });
      setDocuments(response.data);
    } catch (error) { console.error(error); } finally { setLoading(false); }
  };

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files.length) return;
    setUploading(true);

    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }
    formData.append('category', categoryFilter || 'General');

    try {
      await axios.post(`${API_URL}/api/documents/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      fetchDocuments();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al subir archivos');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este documento?')) return;
    try {
      await axios.delete(`${API_URL}/api/documents/${id}`, { withCredentials: true });
      fetchDocuments();
    } catch (error) { alert('Error al eliminar'); }
  };

  const handleAnalyze = async (id) => {
    setAnalyzing(id);
    setAnalysisResult(null);
    try {
      const response = await axios.post(`${API_URL}/api/documents/${id}/analyze`, {}, { withCredentials: true });
      setAnalysisResult(response.data);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al analizar');
    } finally {
      setAnalyzing(null);
    }
  };

  const handleDownload = async (id, filename) => {
    try {
      const response = await axios.get(`${API_URL}/api/documents/${id}/download`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) { alert('Error al descargar'); }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="documents-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Documentos</h1>
          <p className="text-slate-500">Planos, contratos, y archivos del proyecto</p>
        </div>
        <label className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium cursor-pointer"
          data-testid="upload-button">
          {uploading ? <CircleNotch size={20} className="animate-spin" /> : <UploadSimple size={20} weight="bold" />}
          {uploading ? 'Subiendo...' : 'Subir Archivo'}
          <input type="file" multiple className="hidden" onChange={handleUpload} accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xlsx,.txt" />
        </label>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4">
        <FunnelSimple size={20} className="text-slate-400" />
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-4 py-2.5 border border-slate-200 rounded-lg bg-white" data-testid="doc-category-filter">
          <option value="">Todas las categorias</option>
          <option value="Blueprint">Planos</option>
          <option value="Contract">Contratos</option>
          <option value="Proposal">Propuestas</option>
          <option value="Report">Reportes</option>
          <option value="General">General</option>
        </select>
        <span className="text-sm text-slate-500">{documents.length} documentos</span>
      </div>

      {/* AI Analysis Result */}
      {analysisResult && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6" data-testid="analysis-result">
          <h3 className="font-bold text-indigo-900 mb-3">Analisis AI</h3>
          <div className="prose prose-sm text-indigo-800 whitespace-pre-wrap">
            {analysisResult.analysis}
          </div>
          <button onClick={() => setAnalysisResult(null)} className="mt-3 text-sm text-indigo-600 hover:text-indigo-800">Cerrar</button>
        </div>
      )}

      {/* Documents Grid */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16"><CircleNotch size={32} className="animate-spin text-blue-600" /></div>
        ) : documents.length > 0 ? (
          <div className="divide-y divide-slate-100">
            {documents.map((doc) => {
              const FileIcon = getFileIcon(doc.filename);
              return (
                <div key={doc.id} className="flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors" data-testid={`doc-row-${doc.id}`}>
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <FileIcon size={22} className="text-slate-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate">{doc.filename}</p>
                    <div className="flex items-center gap-3 text-xs text-slate-400">
                      <span>{formatFileSize(doc.file_size)}</span>
                      <span>{doc.category || 'General'}</span>
                      <span>{new Date(doc.created_at).toLocaleDateString('es-ES')}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => handleAnalyze(doc.id)} disabled={analyzing === doc.id}
                      className="p-2 hover:bg-indigo-50 rounded-lg text-indigo-600 disabled:opacity-50"
                      title="Analizar con AI" data-testid={`analyze-doc-${doc.id}`}>
                      {analyzing === doc.id ? <CircleNotch size={18} className="animate-spin" /> : <MagnifyingGlass size={18} />}
                    </button>
                    <button onClick={() => handleDownload(doc.id, doc.filename)} className="p-2 hover:bg-slate-100 rounded-lg text-slate-600"
                      title="Descargar" data-testid={`download-doc-${doc.id}`}>
                      <Download size={18} />
                    </button>
                    <button onClick={() => handleDelete(doc.id)} className="p-2 hover:bg-red-50 rounded-lg text-red-500"
                      title="Eliminar" data-testid={`delete-doc-${doc.id}`}>
                      <Trash size={18} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="py-16 text-center">
            <Files size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No hay documentos todavia</p>
            <p className="text-sm text-slate-400 mt-1">Sube planos, contratos o cualquier archivo del proyecto</p>
          </div>
        )}
      </div>
    </div>
  );
}
